import { ProductCard } from 'front-commons/ds';
import { useEffect, useMemo, useState } from 'react';
import useDrawer from 'stores/drawer';
import { PromotionProductProps } from './interfaces';
import { getCurrentRange } from './helpers';
import { getTags } from 'hooks/useCatalogProductRules/helpers';
import useOrderImport from 'stores/orderImport';
import useBasket from 'stores/basket';
import { SimplifiedPromotionCardProps } from 'front-commons/ds/components/ProductCard/variants/SimplifiedPromotionCard/interfaces';

export default function PromotionProduct({
	data,
	posId,
	simpleProductData,
	selectedDistributor,
	updateSummary,
	promotionHandlers,
	onAfterReplaceSourceProduct,
}: PromotionProductProps) {
	const { drawerStore } = useDrawer();
	const { handleGetBasketData } = useBasket();
	const {
		handleOpenDetailsDrawer,
		orderImportStore: { loading },
	} = useOrderImport();

	const {
		fixedAmount,
		productsInCombo,
		hasRangeProduct,
		rangeByQuantity,
		discountToApply,
		progressiveDiscount,
		progressiveDiscountProductMix,
	} = data;

	const [{ minimumQuantity = 0, ranges: childRanges }] = productsInCombo;
	const ranges = data.ranges.length ? data.ranges : childRanges;

	const simpleQuantity = useMemo(() => {
		if (fixedAmount) return 1;
		if (minimumQuantity > simpleProductData.quantity) return minimumQuantity;

		return simpleProductData.quantity;
	}, [simpleProductData.quantity, minimumQuantity, drawerStore]);

	const drawerState = useMemo(() => {
		return drawerStore.drawers.find((drawer) => drawer.id === 'promotion-suggestion')!.open;
	}, [drawerStore]);

	const [quantity, setQuantity] = useState(simpleQuantity || 1);
	const [discount, setDiscount] = useState(0);

	const unityPrice =
		(selectedDistributor.unitPrice! / (fixedAmount ? 1 : simpleProductData.quantity)) * ((100 - discount) / 100);

	const onOpenDetailsDrawer: SimplifiedPromotionCardProps['onOpenDetailsDrawer'] = (props) => {
		if (loading) return Promise.resolve();

		return handleOpenDetailsDrawer({
			...props,
			replaceSource: true,
			initialProductQuantity: { [simpleProductData.id]: quantity },
			handleRefetchItemList: () => {
				handleGetBasketData({ loading: 'refetch' });
			},
			onAfterReplaceSourceProduct,
		});
	};

	useEffect(() => {
		const rangeData = getCurrentRange({
			ranges,
			mixedRanges: ranges,

			fixedAmount,
			hasRangeProduct,
			rangeByQuantity,
			progressiveDiscount,
			progressiveDiscountProductMix,

			productPrices: unityPrice,
			totalPrice: unityPrice * quantity,

			allProductQuantities: quantity,
			quantityInBasket: fixedAmount ? quantity * minimumQuantity : quantity,
			updatedProductQuantity: quantity,
		});

		const newDiscount = ranges?.length ? rangeData.discount : discountToApply;

		updateSummary(simpleProductData.id, {
			quantity,
			discount: newDiscount,
			currentValue: unityPrice * quantity,
		});

		setDiscount(newDiscount);
	}, [quantity]);

	useEffect(() => {
		setQuantity(simpleQuantity);
		promotionHandlers(simpleProductData.id).set.quantity(simpleQuantity);
	}, [drawerState]);

	const payload: SimplifiedPromotionCardProps['data'] = {
		...(data as any as SimplifiedPromotionCardProps['data']),
		maxDiscount:
			data.maxDiscount || data?.ranges?.at(-1)?.discount || data.productsInCombo[0]?.ranges?.at(-1)?.discount || 0,
		fixedAmount,
		hasRangeProduct,
		productsInCombo: data.productsInCombo as any,
		progressiveDiscount,
		progressiveDiscountProductMix,
		rangeByQuantity,
		id: data.id,
		pmc: false,
		hasOtherAvailableDistributor: data.distributors.filter(({ status }) => status !== 'UNAVAILABLE').length > 1,
		businessUnitId: data.businessUnitId,
		businessUnitName: data.businessUnitName,
		hyperaCode: data.hyperaCode || '',
		minQuantityDistinctProducts: 1,
		description: data.description,
		distributors: data.distributors.map((item) => ({
			...item,
			price: item.unitPrice,
			finalPrice: item.unitPricePromotion,
		})),
		limitedByQuantity: data.limitedByQuantity || null,
		limitedByQuantityFilterType: null,
		moreAbout: data.moreAbout,
		endDate: data.endDate,
		ranges: ranges as any,
		totalLimit: data.totalLimit || null,
		taxSubstitution: selectedDistributor.taxSubstitution,
		availableLimit: data.availableLimit || null,
	};

	return (
		<ProductCard
			data={payload}
			tags={getTags(data as any as SimplifiedPromotionCardProps['data'])}
			posId={posId}
			variant="SIMPLIFIED_PROMOTION"
			// @ts-ignore
			productName={simpleProductData.description}
			onOpenDrawer={() => {}}
			imageProvider={import.meta.env.VITE_STATIC_STORAGE_BASE}
			sourceProductId={simpleProductData.id}
			onOpenDetailsDrawer={onOpenDetailsDrawer}
			orderImportProductId=""
			currentDistributor={{
				...selectedDistributor,
				price: selectedDistributor.unitPrice,
				finalPrice: selectedDistributor.unitPricePromotion,
			}}
		/>
	);
}
